import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { Plus, Trash2, Users as UsersIcon, Shield, Edit2, Eye, Ban, CheckCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const Users = () => {
  const { t, API, user: currentUser, BACKEND_URL } = useApp();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    login: '',
    nickname: '',
    email: '',
    password: '',
    profile_type: 'viewer',
  });
  const [editFormData, setEditFormData] = useState({
    nickname: '',
    email: '',
    profile_type: '',
    is_active: true,
    password: '',
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.login || !formData.nickname || !formData.email || !formData.password) {
      toast.error(t('errors.requiredField'));
      return;
    }

    try {
      await axios.post(`${API}/users`, formData);
      toast.success(t('common.success'));
      setShowCreateDialog(false);
      setFormData({ login: '', nickname: '', email: '', password: '', profile_type: 'viewer' });
      fetchUsers();
    } catch (error) {
      console.error("Update error:", error);
      const detail = error.response?.data?.detail;
      
      // Cas 1: Erreur de validation Pydantic (tableau d'objets)
      if (Array.isArray(detail)) {
        // On construit un message lisible à partir du tableau
        const message = detail.map(err => {
            // err.loc est un tableau ex: ["body", "nickname"]
            const field = err.loc[err.loc.length - 1]; 
            return `${field}: ${err.msg}`;
        }).join('\n');
        toast.error(message);
      } 
      // Cas 2: Erreur simple (string) ex: "User not found"
      else if (typeof detail === 'string') {
        toast.error(detail);
      } 
      // Cas 3: Fallback
      else {
        toast.error(t('errors.serverError'));
      }
    }

  };

  const handleEdit = async () => {
    if (!selectedUser) return;

    // On n'envoie le mot de passe que s'il a été saisi (sinon on le retire du payload)
    const payload = { ...editFormData };
    if (!payload.password) delete payload.password;

    try {
      await axios.put(`${API}/users/${selectedUser.id}`, payload);
      toast.success(t('common.success'));
      setShowEditDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Update error:", error);
      const detail = error.response?.data?.detail;
      
      // Cas 1: Erreur de validation Pydantic (tableau d'objets)
      if (Array.isArray(detail)) {
        // On construit un message lisible à partir du tableau
        const message = detail.map(err => {
            // err.loc est un tableau ex: ["body", "nickname"]
            const field = err.loc[err.loc.length - 1]; 
            return `${field}: ${err.msg}`;
        }).join('\n');
        toast.error(message);
      } 
      // Cas 2: Erreur simple (string) ex: "User not found"
      else if (typeof detail === 'string') {
        toast.error(detail);
      } 
      // Cas 3: Fallback
      else {
        toast.error(t('errors.serverError'));
      }
    }

  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success(t('common.success'));
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditFormData({
      nickname: user.nickname,
      email: user.email,
      profile_type: user.profile_type,
      is_active: user.is_active !== false,
      password: '',
    });
    setShowEditDialog(true);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'administrator': return 'bg-primary/20 text-primary border-primary/30';
      case 'editor': return 'bg-violet-500/20 text-violet-400 border-violet-500/30';
      case 'viewer': return 'bg-muted text-muted-foreground border-border';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="eyebrow text-[11px] text-primary/80 mb-2">Arcade Fleet · Stock Manager</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-[0.08em] uppercase sunset-text">
            {t('users.title')}
          </h1>
          <p className="text-muted-foreground mt-2 font-mono text-xs">
            {users.length} {t('users.title').toLowerCase()}
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="font-display uppercase font-bold tracking-[0.16em] glow-amber" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('users.addUser')}
        </Button>
      </div>

      {/* Users Table */}
      <Card className="bezel">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="eyebrow text-[10px] text-muted-foreground">{t('users.avatar')}</TableHead>
                <TableHead className="eyebrow text-[10px] text-muted-foreground">{t('users.login')}</TableHead>
                <TableHead className="eyebrow text-[10px] text-muted-foreground">{t('users.nickname')}</TableHead>
                <TableHead className="eyebrow text-[10px] text-muted-foreground">{t('users.email')}</TableHead>
                <TableHead className="eyebrow text-[10px] text-muted-foreground">{t('users.role')}</TableHead>
                <TableHead className="eyebrow text-[10px] text-muted-foreground">Status</TableHead>
                <TableHead className="eyebrow text-[10px] text-muted-foreground">{t('users.createdAt')}</TableHead>
                <TableHead className="eyebrow text-[10px] text-muted-foreground text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} className="hover:bg-muted/40 transition-colors" data-testid={`user-row-${user.id}`}>
                  <TableCell>
                    <Avatar className="w-10 h-10">
                      <AvatarImage src={user.avatar ? `${user.avatar}` : undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary text-sm">
                        {user.nickname?.slice(0, 2).toUpperCase() || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell className="font-mono">{user.login}</TableCell>
                  <TableCell className="font-medium">{user.nickname}</TableCell>
                  <TableCell className="font-mono text-sm">{user.email}</TableCell>
                  <TableCell>
                    <Badge className={`${getRoleBadgeColor(user.profile_type)} uppercase text-xs font-bold`}>
                      <Shield className="w-3 h-3 mr-1" />
                      {t(`users.${user.profile_type}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.is_active === false ? (
                      <Badge variant="outline" className="bg-red-500/20 text-red-500 border-red-500/30">
                        <Ban className="h-3 w-3 mr-1" />
                        Inactive
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Active
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm text-muted-foreground">{formatDate(user.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      {user.id !== currentUser?.id && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => { setSelectedUser(user); setShowDeleteDialog(true); }}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('users.addUser')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('users.login')} *</Label>
              <Input
                value={formData.login}
                onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                placeholder={t('users.login')}
                className="font-mono"
                data-testid="user-login-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('users.nickname')} *</Label>
              <Input
                value={formData.nickname}
                onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                placeholder={t('users.nickname')}
                data-testid="user-nickname-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('users.email')} *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder={t('users.email')}
                className="font-mono"
                data-testid="user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('auth.password')} *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={t('auth.password')}
                className="font-mono"
                data-testid="user-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('users.role')} *</Label>
              <Select value={formData.profile_type} onValueChange={(v) => setFormData({ ...formData, profile_type: v })}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="administrator">{t('users.administrator')}</SelectItem>
                  <SelectItem value="editor">{t('users.editor')}</SelectItem>
                  <SelectItem value="viewer">{t('users.viewer')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleCreate} className="font-display uppercase tracking-[0.14em] glow-amber" data-testid="create-user-submit">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('users.editUser')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('users.nickname')}</Label>
              <Input
                value={editFormData.nickname}
                onChange={(e) => setEditFormData({ ...editFormData, nickname: e.target.value })}
                data-testid="edit-user-nickname-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('users.email')}</Label>
              <Input
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                className="font-mono"
                data-testid="edit-user-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('users.role')}</Label>
              <Select value={editFormData.profile_type} onValueChange={(v) => setEditFormData({ ...editFormData, profile_type: v })}>
                <SelectTrigger data-testid="edit-user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="administrator">{t('users.administrator')}</SelectItem>
                  <SelectItem value="editor">{t('users.editor')}</SelectItem>
                  <SelectItem value="viewer">{t('users.viewer')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">{t('users.newPassword')}</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={editFormData.password}
                onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                placeholder="••••••••"
                className="font-mono"
                data-testid="edit-user-password-input"
              />
              <p className="text-xs text-muted-foreground">{t('users.newPasswordHint')}</p>
            </div>
                        <div className="space-y-2">
              <Label className="eyebrow text-[10px] text-muted-foreground">Account Status</Label>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="inactive-checkbox"
                  // Si is_active est true (actif), la case "Inactive" doit être décochée (false)
                  // Si is_active est false (inactif), la case "Inactive" doit être cochée (true)
                  checked={!editFormData.is_active}
                  
                  // Quand on change :
                  // Si on coche (e.target.checked = true) -> is_active devient false
                  // Si on décoche (e.target.checked = false) -> is_active devient true
                  onChange={(e) => setEditFormData({...editFormData, is_active: !e.target.checked})}
                  
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <label htmlFor="inactive-checkbox" className="text-sm cursor-pointer select-none">
                  Inactive (Check to disable account)
                </label>
              </div>
            </div>

          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleEdit} className="font-display uppercase tracking-[0.14em] glow-amber" data-testid="edit-user-submit">{t('common.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('users.deleteUser')}</AlertDialogTitle>
            <AlertDialogDescription>{t('users.confirmDelete')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive" data-testid="confirm-delete-user">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Users;
